import React from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { GoogleAuth } from '../utils/nativeGoogleAuth';
import { GoogleIcon } from './GoogleIcon';
import { startGoogleSignIn, startGoogleSignInIOSRedirect } from '../utils/googleAuth';
import { getDefaultRoute } from '../utils/defaultRoute';
import { identifyAirbridgeUser, trackSignin } from '../utils/airbridge';
import { persistAuthSession } from '../utils/authStorage';
import { isIOSDevice, isStandalonePWA } from '../utils/iosInstallContext';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const USE_CUSTOM_OAUTH = String(process.env.REACT_APP_USE_CUSTOM_GOOGLE_OAUTH || '').toLowerCase() === 'true';

// Detect Capacitor NATIVE runtime only (Android/iOS native app).
// The Capacitor web shim injects window.Capacitor even on plain web / PWA,
// so `if (window.Capacitor)` alone is a false positive on every browser.
// Use Capacitor.isNativePlatform() (returns true only for android/ios) and
// fall back to getPlatform() to be safe across Capacitor versions.
// Fallback 1: appendUserAgent in capacitor.config.json adds 'AllureApp' to the UA string.
// Fallback 2: Android WebView always includes '; wv)' or 'wv' in the UA (Chrome does not).
const isCapacitorWebView = () => {
  if (typeof window === 'undefined') return false;
  const cap = window.Capacitor;
  if (cap) {
    try {
      if (typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) return true;
      if (typeof cap.getPlatform === 'function') {
        const p = cap.getPlatform();
        if (p === 'android' || p === 'ios') return true;
      }
    } catch (_) {
      /* fall through to UA sniffing */
    }
  }
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (ua.includes('AllureApp')) return true;
  if (/\bwv\b/i.test(ua) || ua.includes('; wv)') || ua.includes(' wv ')) return true;
  return false;
};

// Detect iOS Safari or iOS PWA (standalone), but NOT the Capacitor native app
// (Capacitor native is handled separately above). Google Identity Services
// popups (used by @react-oauth/google) are unreliable in mobile Safari and
// completely broken in a standalone iOS PWA — the popup either fails to
// broadcast back to the opener or is blocked outright, leaving the button
// stuck on "Opening Google…" forever. On iOS we must use a full-page
// redirect instead.
const isIOSWebOrPWA = () => {
  if (typeof window === 'undefined') return false;
  if (isCapacitorWebView()) return false; // native iOS handled by Capacitor plugin
  return isIOSDevice() || isStandalonePWA();
};

// Single button used by Login and Register screens.
// - On Capacitor Android: uses the NATIVE @capgo/capacitor-social-login
//   plugin. Google Play Services shows its own bottom-sheet account picker,
//   no external browser opens, and the plugin returns an idToken +
//   accessToken directly. We POST the accessToken to the existing
//   /api/auth/google/oauth-token endpoint — same shape as web flow — so
//   backend code and downstream routing stay untouched.
// - On iOS Safari / iOS PWA: uses a full-page redirect to accounts.google.com
//   (popups are blocked / postMessage-broken on iOS). The existing
//   GoogleAuthCallback page picks up the access_token from the URL fragment.
// - On web: uses the @react-oauth/google popup flow (when USE_CUSTOM_OAUTH
//   is on) or falls back to the Emergent-managed redirect.
export function GoogleSignInButton({ onLogin, label = 'Continue with Google', dataTestId = 'google-signin-button', style }) {
  if (isCapacitorWebView()) {
    return <NativeGoogleSignInButton onLogin={onLogin} label={label} dataTestId={dataTestId} style={style} />;
  }
  if (isIOSWebOrPWA()) {
    return <IOSRedirectGoogleButton label={label} dataTestId={dataTestId} style={style} />;
  }
  if (USE_CUSTOM_OAUTH && process.env.REACT_APP_GOOGLE_CLIENT_ID) {
    return (
      <CustomGoogleButton onLogin={onLogin} label={label} dataTestId={dataTestId} style={style} />
    );
  }
  return (
    <button
      type="button"
      onClick={startGoogleSignIn}
      data-testid={dataTestId}
      className="auth-secondary-btn auth-fade-up"
      style={style}
    >
      <GoogleIcon size={20} />
      {label}
    </button>
  );
}

// iOS Safari / iOS PWA sign-in button.
// Fires a full-page redirect on tap. We render our own "Opening Google…"
// state on tap so the user gets immediate feedback while Safari transitions
// to accounts.google.com — the redirect itself can take ~500ms on slow
// networks and without this the tap feels unresponsive.
function IOSRedirectGoogleButton({ label, dataTestId, style }) {
  const [busy, setBusy] = React.useState(false);
  const handleTap = React.useCallback(() => {
    if (busy) return;
    setBusy(true);
    // Kick off the redirect synchronously so Safari treats it as part of
    // the user gesture — this avoids the "navigation blocked" bar some
    // iOS versions show when window.location.href is set from an async
    // callback long after the tap.
    startGoogleSignInIOSRedirect();
  }, [busy]);
  return (
    <button
      type="button"
      onClick={handleTap}
      disabled={busy}
      data-testid={dataTestId}
      className="auth-secondary-btn auth-fade-up"
      style={style}
    >
      <GoogleIcon size={20} />
      {busy ? 'Opening Google…' : label}
    </button>
  );
}

// Native Google Sign-In inside the Capacitor Android WebView.
//
// The plugin is auto-registered by Capacitor 6, but the JS binding still
// needs a one-time initialize() call before signIn() will work reliably
// on Android. We init lazily on the first tap so the bootstrap only pays
// its cost when the user actually wants to sign in.
//
// Server-side: we send `authentication.accessToken` to the existing
// /api/auth/google/oauth-token endpoint. The backend already validates the
// access token by calling Google's userinfo endpoint, so no server change
// is needed.
function NativeGoogleSignInButton({ onLogin, label, dataTestId, style }) {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const initedRef = React.useRef(false);

  const initOnce = React.useCallback(async () => {
    if (initedRef.current) return;
    try {
      await GoogleAuth.initialize({
        scopes: ['profile', 'email'],
        grantOfflineAccess: false,
      });
      initedRef.current = true;
    } catch (err) {
      console.warn('GoogleAuth.initialize failed (safe to retry on next tap)', err);
    }
  }, []);

  const handleTap = React.useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await initOnce();
      // Force the native account picker every time so users can switch
      // accounts. Without this, Google Play Services silently reuses the
      // last selected account after the first successful sign-in and the
      // picker never re-appears.
      try { await GoogleAuth.signOut(); } catch (_) { /* ignore — no active session */ }
      const result = await GoogleAuth.signIn();
      const accessToken = result?.authentication?.accessToken;
      if (!accessToken) {
        throw new Error('Native Google sign-in returned no access token.');
      }
      const res = await axios.post(`${API}/auth/google/oauth-token`, {
        access_token: accessToken,
      });
      if (res.data.needs_profile) {
        navigate('/google/complete', {
          state: {
            signupToken: res.data.signup_token,
            email: res.data.email,
            suggestedFirstName: res.data.suggested_first_name || '',
            suggestedLastName: res.data.suggested_last_name || '',
          },
        });
        return;
      }
      await persistAuthSession(res.data.access_token, res.data.user);
      onLogin?.(res.data.user);
      identifyAirbridgeUser(res.data.user);
      trackSignin({ user: res.data.user, method: 'google' });
      toast.success('Welcome back!');
      navigate(getDefaultRoute(res.data.user), { replace: true });
    } catch (err) {
      // The plugin throws when the user cancels the native picker (or
      // when Google Play Services is missing / signed in with an
      // unregistered SHA-1). Distinguish cancellations from real errors
      // so we don't spam the user with red toasts they caused themselves.
      const message = String(err?.message || err || '');
      // Only treat explicit cancel signals as cancellations — do NOT swallow
      // DEVELOPER_ERROR (code 10) which historically matched the plain "10" regex.
      const cancelled = /cancel|user_cancel|CANCELED|SIGN_IN_CANCELLED|12501/i.test(message);
      if (!cancelled) {
        const detail = err?.response?.data?.detail;
        toast.error(
          typeof detail === 'string'
            ? detail
            : 'Google sign-in failed. Please try again.'
        );
        console.warn('Native Google sign-in error', err);
      }
    } finally {
      setBusy(false);
    }
  }, [busy, initOnce, navigate, onLogin]);

  return (
    <button
      type="button"
      onClick={handleTap}
      disabled={busy}
      data-testid={dataTestId}
      className="auth-secondary-btn auth-fade-up"
      style={{ width: '100%', ...style }}
    >
      <GoogleIcon size={20} />
      {busy ? 'Opening Google…' : label}
    </button>
  );
}

// Used on web when USE_CUSTOM_OAUTH=true.
// Falls back to a plain redirect-based Google Sign-In (external browser)
// only when the popup is blocked (very rare in modern desktop browsers).
function CustomGoogleButton({ onLogin, label, dataTestId, style }) {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);

  const login = useGoogleLogin({
    flow: 'implicit',
    scope: 'openid email profile',
    onSuccess: async (tokenResponse) => {
      try {
        const res = await axios.post(`${API}/auth/google/oauth-token`, {
          access_token: tokenResponse.access_token,
        });
        if (res.data.needs_profile) {
          navigate('/google/complete', {
            state: {
              signupToken: res.data.signup_token,
              email: res.data.email,
              suggestedFirstName: res.data.suggested_first_name || '',
              suggestedLastName: res.data.suggested_last_name || '',
            },
          });
          return;
        }
        await persistAuthSession(res.data.access_token, res.data.user);
        onLogin?.(res.data.user);
        identifyAirbridgeUser(res.data.user);
        trackSignin({ user: res.data.user, method: 'google' });
        toast.success('Welcome back!');
        navigate(getDefaultRoute(res.data.user), { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Google sign-in failed.');
      } finally {
        setBusy(false);
      }
    },
    onError: (err) => {
      setBusy(false);
      console.warn('Google sign-in error', err);
      toast.error('Google sign-in was cancelled.');
    },
    onNonOAuthError: () => {
      setBusy(false);
      // If popup was blocked (very rare on desktop), fall back to the
      // Emergent-managed redirect so the user isn't stranded.
      startGoogleSignIn();
    },
  });

  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => { setBusy(true); login(); }}
      data-testid={dataTestId}
      className="auth-secondary-btn auth-fade-up"
      style={style}
    >
      <GoogleIcon size={20} />
      {busy ? 'Opening Google…' : label}
    </button>
  );
}
